/**
 * The nav catalog: the sidebar pages a lens may name, by stable id.
 *
 * Lens manifests (served by the Agent Atlas service, see config/lenses) name
 * pages only by these ids, so a lens can never link a page the sidebar does
 * not: catalog.test.js checks every `to` here is a link in the classic sidebar,
 * and that every classic-sidebar link is reachable from some lens.
 *
 * Only cross-product pages are listed (Agent Atlas, its Security pages, the
 * Management pages lenses pull forward). A product module (Endpoint Security,
 * Code Security, Copilot, ...) is named whole as a section, and the sidebar
 * draws it with its own renderer and gates, so its rows are not repeated here.
 *
 * `requires` mirrors the sidebar row's gate and the route's ProtectedRoute:
 *   { feature }       the feature flag is on for the user
 *   { module }        the module slug the page belongs to (an unlicensed one
 *                     still shows, locked; the org owning it decides whether
 *                     a lens is worth offering — lensAccess `owns`)
 *   { anyModule: [] } one of these slugs
 *   { admin: true }   an administrator (the sidebar's Management rule)
 *   { orgIds: [] }    the user's organization is one of these
 *   [gate, gate]      every gate passes
 *   null              signed in
 *
 * `section` is the classic-sidebar section the row sits in. A page shows in a
 * lens only when that section would show too.
 *
 * Labels are nav-namespace keys, the same strings the classic sidebar shows.
 */


// Agent Atlas is a core module: its pages carry no gate (the AGENT_ATLAS flag
// that used to gate them was '*' and duplicated that).
const ATLAS = null;
const SECURITY = { module: 'security' };
const DEEPFAKE = { module: 'deepfake' };
// Red-team audits, scheduled scans and model evaluation: the `redteam` module.
const ASSESSMENT = { module: 'redteam' };
const ADMIN = { admin: true };

const page = (to, labelKey, icon, requires, section) => ({ to, labelKey, icon, requires, section });

export const NAV_PAGES = Object.freeze({
  // Agent Atlas (classic sidebar section `agent-atlas`).
  'atlas.inbox': page('/agent-atlas/inbox', 'items.recommendations', 'inbox', [ATLAS, { feature: 'RECOMMENDATIONS' }], 'agent-atlas'),
  'atlas.map': page('/agent-atlas/atlas', 'items.atlas', 'map', ATLAS, 'agent-atlas'),
  'atlas.fleet': page('/agent-atlas/inventory', 'items.agentFleet', 'list', ATLAS, 'agent-atlas'),
  'atlas.users': page('/agent-atlas/users', 'items.users', 'users', ATLAS, 'agent-atlas'),
  'atlas.conversations': page('/agent-atlas/adoption/conversations', 'items.conversations', 'message-square', ATLAS, 'agent-atlas'),
  'atlas.knowledge-gaps': page('/agent-atlas/adoption/knowledge-gaps', 'items.knowledgeGaps', 'help-circle', ATLAS, 'agent-atlas'),
  'atlas.intent': page('/agent-atlas/adoption/intent-analysis', 'items.intentAnalysis', 'target', ATLAS, 'agent-atlas'),
  'atlas.assessment': page('/agent-atlas/assessment', 'sections.assessment', 'check-square', [ATLAS, ASSESSMENT], 'agent-atlas'),
  'atlas.red-team': page('/agent-atlas/assessment/red-team', 'items.redTeamAudit', 'shield', [ATLAS, ASSESSMENT], 'agent-atlas'),
  'atlas.schedules': page('/agent-atlas/assessment/schedules', 'items.scheduledScans', 'clock', [ATLAS, ASSESSMENT], 'agent-atlas'),
  'atlas.evaluations': page('/agent-atlas/assessment/evaluations', 'items.modelEvaluation', 'box', [ATLAS, ASSESSMENT], 'agent-atlas'),
  'atlas.compliance': page('/agent-atlas/compliance', 'items.compliance', 'clipboard', ATLAS, 'agent-atlas'),
  // Named as in every module's Compliance group (2026-10-20); with the compliance platform the
  // page itself is headed Audit & documents.
  'atlas.compliance-audit': page('/agent-atlas/compliance-audit', 'items.auditReport', 'file-text', ATLAS, 'agent-atlas'),
  'atlas.log-explorer': page('/agent-atlas/log-explorer', 'items.logExplorer', 'file-text', ATLAS, 'agent-atlas'),
  'atlas.policy': page('/agent-atlas/policy', 'items.policy', 'lock', ATLAS, 'agent-atlas'),
  'atlas.alerts': page('/agent-atlas/alerts', 'items.alerts', 'bell', ATLAS, 'agent-atlas'),
  'atlas.finops': page('/agent-atlas/finops', 'items.finops', 'pie-chart', ATLAS, 'agent-atlas'),

  // Security, the expander inside Agent Atlas. The routes need the security module.
  'security.overview': page('/security-atlas/overview', 'lens.items.securityOverview', 'shield', [ATLAS, SECURITY], 'agent-atlas'),
  'security.signals': page('/security-atlas/signals', 'items.signals', 'activity', [ATLAS, SECURITY], 'agent-atlas'),
  'security.hunt': page('/security-atlas/hunt', 'items.hunt', 'search', [ATLAS, SECURITY], 'agent-atlas'),
  'security.incidents': page('/security-atlas/incidents', 'items.incidents', 'alert-triangle', [ATLAS, SECURITY], 'agent-atlas'),
  'security.response': page('/security-atlas/response', 'items.response', 'zap', [ATLAS, SECURITY], 'agent-atlas'),
  'security.deepfake': page('/security-atlas/deepfake-detector', 'items.deepfakeDetector', 'eye', [ATLAS, DEEPFAKE], 'agent-atlas'),
  'security.skills': page('/security-atlas/skills', 'items.skills', 'layers', [ATLAS, SECURITY], 'agent-atlas'),
  'security.ai-bom': page('/security-atlas/ai-bom', 'items.aiBomInventory', 'package', [ATLAS, SECURITY], 'agent-atlas'),
  // preview, org 3 only, until MCP capture ships
  'security.mcp': page('/security-atlas/mcp-security', 'items.mcpSecurity', 'server', [ATLAS, SECURITY, { orgIds: [3] }], 'agent-atlas'),
  'security.agent-instructions': page('/security-atlas/agent-instructions', 'items.agentInstructions', 'file-text', [ATLAS, SECURITY, { orgIds: [3] }], 'agent-atlas'),
  'security.data-security': page('/security-atlas/data-security', 'items.dataSecurity', 'database', [ATLAS, SECURITY], 'agent-atlas'),
  'security.exceptions': page('/security-atlas/exceptions', 'items.exceptions', 'check-circle', [ATLAS, SECURITY], 'agent-atlas'),

  // Custom Agents' guardrails page: the one Custom Agents row a lens names alone.
  'custom.guardrails': page('/admin/custom-guardrails', 'items.customGuardrails', 'sliders', SECURITY, 'custom-agents'),

  // Management (administrators only, as the section is).
  'admin.automation': page('/security-atlas/automation', 'lens.items.socConfiguration', 'sliders', [ADMIN, SECURITY], 'management'),
  'admin.recommendation-rules': page('/admin/recommendation-rules', 'items.recommendationRules', 'target', [ADMIN, { orgIds: [3] }], 'management'),
  'admin.audit-log': page('/admin/audit-log', 'items.platformAuditLog', 'file-text', ADMIN, 'management'),
  'admin.data-policy': page('/admin/data-policy', 'items.dataPolicy', 'shield', ADMIN, 'management'),
  'admin.ai-provider': page('/admin/ai-provider', 'items.aiProvider', 'cpu', ADMIN, 'management'),
});

/**
 * Classic-sidebar sections a lens may show whole, with the gates the sidebar
 * applies to each on top of the org's module entitlement (productConfig's
 * SECTION_MODULE_SLUGS). The product a section belongs to supplies its home.
 *
 * `surface: true` marks the sections a user can pick in the Surfaces list (the
 * multi-select that replaced the product dropdown), with the name the sidebar
 * heading uses. The rest are always shown (Management, dashboards). Billing &
 * Usage is not a section: it is in the header's account menu, for org admins.
 */
export const NAV_SECTIONS = Object.freeze({
  // Surfaces, in the order the classic sidebar draws them (the Surfaces list
  // follows this order; contract.test.js keeps the two in step).
  'agent-atlas': { requires: ATLAS, surface: true, label: 'Agent Atlas', labelKey: 'nav:sections.agentAtlas' },
  'cloud-ai': { requires: null, surface: true, label: 'Cloud Discovery', labelKey: 'nav:items.cloudDiscovery' },
  'custom-agents': { requires: null, surface: true, label: 'Custom Agents', labelKey: 'nav:items.customAgents' },
  'shadow-ai': { requires: { feature: 'SHADOW_AI' }, surface: true, label: 'Endpoint Security', labelKey: 'nav:items.endpointSecurity' },
  'copilot-studio': { requires: null, surface: true, label: 'Copilot Studio', labelKey: 'nav:items.copilotStudio' },
  'enterprise-ai': { requires: null, surface: true, label: 'Microsoft 365 Copilot', labelKey: 'nav:items.m365Copilot' },
  'gemini-enterprise-agents': { requires: null, surface: true, label: 'Gemini Enterprise', labelKey: 'nav:items.geminiEnterprise' },
  'gemini-workspace': { requires: null, surface: true, label: 'Gemini Workspace', labelKey: 'nav:items.geminiWorkspace' },
  'saas-ai-platform': { requires: null, surface: true, label: 'AI Providers', labelKey: 'nav:items.aiProviders' },
  sast: { requires: null, surface: true, label: 'Code Security', labelKey: 'nav:items.codeSecurity' },
  management: { requires: ADMIN },
  'custom-dashboards': { requires: null },
});

export const navPage = (id) => NAV_PAGES[id] || null;
